/**
 * Lift2Fit — Help (`#/help`).
 *
 * The lifter shell's door to the support thread. Coaches meet the same thread
 * pinned inside the chat workspace; every other account — and every coach who
 * is not in chat — reaches it here. It is one conversation per account, never an
 * inbox of its own: the platform side lives in the admin portal.
 */
import { HelpConversation } from '../crm/HelpConversation';
import { HelpFeedbackPanel } from '../crm/HelpFeedbackPanel';
import { AppLinkButton, Panel, ScreenHeader } from '../components';
import { ChatIcon } from '../chat';
import { useSession } from '@/lib/useSession';

export function HelpScreen() {
  const session = useSession();

  if (!session.user) {
    return (
      <div className="flex flex-col gap-4 border border-hairline bg-surface p-6" data-testid="help-signed-out">
        <h1 className="font-display text-2xl font-black uppercase text-ink">Help</h1>
        <p className="text-[15px] text-ink-muted">
          Sign in and your help thread is already here — every account has one.
        </p>
        <AppLinkButton to="/login" variant="outline">
          Sign in
        </AppLinkButton>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6" data-testid="help-screen">
      <ScreenHeader
        eyebrow="Support"
        title="Help"
        lead="One thread, already open for this account. Ask about billing, a bug, or how something works and the team answers here."
        badge={
          <span className="inline-flex items-center gap-1.5 border border-hairline bg-surface px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.08em] text-accent">
            <ChatIcon className="h-3.5 w-3.5" />
            Help
          </span>
        }
      />
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(280px,340px)]">
        <Panel
          shape="chat"
          testId="help-panel"
          bodyClassName="gap-3"
          title="Your conversation with the platform"
          subtitle="The same thread appears in chat for a coaching account. Messages are visible to the Lift2Fit team only."
        >
          {/* A bounded height lets the message list scroll instead of pushing the
              composer off the page on a long thread. */}
          {/* No wash behind the bubbles: the fill pair (page-subtle vs
              bubble-mine) is the 0028 separation, so the thread sits on the
              same surface the workspace uses. */}
          <div className="flex h-[min(62vh,560px)] flex-col px-1">
            <HelpConversation testId="help-page" withIdentity={false} />
          </div>
        </Panel>
        {/* 0037 — the side of the thread is about Lift2Fit, not about training. */}
        <HelpFeedbackPanel testId="help-page-feedback" />
      </div>
    </div>
  );
}
